import { useState, useEffect } from 'react';
import api, { urlDocument } from 'services/api';
import { apercuDocument } from 'services/documents';
import BoutonTelechargerDocument from 'components/shared/BoutonTelechargerDocument';
import useEscapeClose from 'hooks/useEscapeClose';
import { limitePour, preparerFichier, formaterPoids } from 'services/televersement';
import { useTenant } from 'context/TenantContext';

// ⚠️ SEUIL CONSERVATEUR, ASSUME (decision Dom, 31/08/2026). Les documents emis
//    AVANT cette date portent un AVERTISSEMENT, pas une accusation : la date
//    exacte ou le generateur est devenu tenant-aware est une reserve ouverte
//    depuis le 19/08. On ne peut donc pas affirmer qu'un document du 29/08 est
//    fautif — on peut affirmer qu'il n'a pas ete verifie.
const SEUIL_VERIF_IDENTITE = new Date('2026-08-31T00:00:00Z');

// ⚠️ `ESSAI_INFO` RETIREE LE 05/09/2026 — c'etait une QUATRIEME source pour la
//    periode d'essai (apres `postes`, `fiches_poste` et `ESSAI_PAR_POSTE` cote
//    serveur), avec un repli silencieux `|| '1 + 1 mois'` qui affichait une
//    duree pour tout poste absent de la liste — donc pour les cinq postes de
//    VIGIE. La periode d'essai se lit sur le POSTE, un seul endroit.

function expirationBadge(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  const now = new Date();
  const diff = Math.ceil((d - now) / (1000 * 60 * 60 * 24));
  if (diff < 0) return <span className="gss-badge" style={{ background: 'rgba(220,38,38,0.08)', color: '#DC2626', fontSize: '0.65rem' }}>Expire</span>;
  if (diff <= 30) return <span className="gss-badge" style={{ background: 'rgba(220,38,38,0.08)', color: '#DC2626', fontSize: '0.65rem' }}>&lt; 30j</span>;
  if (diff <= 60) return <span className="gss-badge" style={{ background: 'rgba(245,158,11,0.08)', color: '#D97706', fontSize: '0.65rem' }}>&lt; 60j</span>;
  return null;
}

/* ⚠️ LE FORMULAIRE NE PRÉ-REMPLIT PLUS AUCUN MONTANT (04/09/2026).
 * ═══════════════════════════════════════════════════════════════════════════
 * AVANT : `salaire_base: 52500, lieu_travail: 'Lome'` — DEUX littéraux écrits
 * dans le code, à deux endroits (état initial + réinitialisation).
 *
 * 52 500 est le SMIG togolais, mais ce n'était PAS une lecture du SMIG : la
 * valeur du tenant existe (`tenants.config->>'smig'`, = 52500 chez FOX,
 * **VIDE chez GLADIATOR et VIGIE**) et n'était jamais consultée. C'était un
 * REPLI, et il s'écrivait EN BASE — pas un défaut d'affichage.
 *
 * CE QUE ÇA A PRODUIT, mesuré en PRODUCTION le 04/09 :
 *   · 7 contrats de travail sur 9 à 52 500 pile
 *   · 81 agents FOX sur 81 renseignés à 52 500 pile (min = max = moyenne)
 *   · 163 bulletins de paie sur 165 à 52 500
 *   · VIGIE, qui n'a jamais utilisé cet écran : 55 000 à 130 000, vraie dispersion
 *
 * ➡️ LE COÛT RÉEL N'EST PAS LE MONTANT, C'EST QU'ON NE PEUT PLUS DISTINGUER
 *    « agent réellement au SMIG » de « champ jamais saisi qui a hérité du
 *    défaut ». La donnée ne dit plus si elle a été DÉCIDÉE ou HÉRITÉE.
 *
 * DÉSORMAIS : le salaire du contrat se REPREND DE LA FICHE AGENT (source
 * unique) à la sélection de l'agent. Si la fiche n'en porte pas, le champ
 * reste VIDE et la route refuse en nommant ce qui manque. On n'invente pas
 * un salaire dans une pièce qui engage l'employeur.
 */
/* 🔴 `poste: ''` RETIRÉ DES TROIS ÉTATS INITIAUX — 13/10/2026 (C2b).
 * ⚠️ L'ÉCRAN PRÉ-SÉLECTIONNAIT UN POSTE QUI N'EST PAS CELUI DU CLIENT. Les postes
 *    sont de la DONNÉE TENANT : une société qui n'a jamais déclaré
 *    `agent_securite` voyait son formulaire déjà rempli avec ce code, et
 *    l'envoyait. Le serveur le refuse désormais en nommant les postes déclarés
 *    — mais **un refus provoqué par un défaut du formulaire reste un défaut du
 *    formulaire**. C'est exactement la liste de postes en dur retirée de l'import
 *    le 17/09, qui survivait ici.
 * ⓘ Le champ est `required` et porte une option vide « — choisir un poste — » :
 *   le formulaire refuse donc côté écran tant que rien n'est choisi, et la liste
 *   vient de `/postes`, le référentiel DU TENANT. */
const VIDE = {
  agent_id: '', poste: '', type_contrat: 'CDI',
  date_debut: '', date_fin: '',
  salaire_base: '',      // JAMAIS de défaut : il vient de la fiche agent
  lieu_travail: '',      // JAMAIS de défaut : il vient de la ville du tenant
  numero_cni: '', clauses_particulieres: '',
};

export default function ContratsTravail() {
  const [tab, setTab] = useState('contrats');
  const [contrats, setContrats] = useState([]);
  const [fiches, setFiches] = useState([]);
  const [agents, setAgents] = useState([]);
  const [alertes, setAlertes] = useState([]);
  const branding = useTenant();
  const devise = branding && branding.devise;
  const ville = branding && branding.ville;
  const [showForm, setShowForm] = useState(false);
  const [showRattacher, setShowRattacher] = useState(false);
  const [modele, setModele] = useState(null);
  const [scan, setScan] = useState(null);
  const [regleScan, setRegleScan] = useState(null);
  const [papier, setPapier] = useState({
    agent_id: '', type_contrat: 'CDI', date_signature: '', date_debut: '', date_fin: '',
    salaire_base: '', poste: '', lieu_travail: '', numero_cni: '',
  });
  const [erreurPapier, setErreurPapier] = useState('');
  const [envoiPapier, setEnvoiPapier] = useState(false);

  useEffect(() => {
    let vivant = true;
    limitePour('scan_contrat').then((r) => { if (vivant) setRegleScan(r); });
    api.get('/contrats-travail/modele-disponible')
      .then((r) => { if (vivant) setModele(r.data); })
      .catch(() => { /* l'ecran ne propose alors AUCUN bouton de generation */ });
    return () => { vivant = false; };
  }, []);

  const choisirScan = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    const { fichier, erreur } = await preparerFichier(f, 'scan_contrat');
    if (erreur) { setErreurPapier(erreur); setScan(null); e.target.value = ''; return; }
    setErreurPapier('');
    setScan(fichier);
  };

  const soumettrePapier = async (e) => {
    e.preventDefault();
    if (envoiPapier) return;
    if (!scan) { setErreurPapier('Le scan du contrat est obligatoire : un contrat papier sans scan n’est rattaché à rien.'); return; }
    setEnvoiPapier(true); setErreurPapier('');
    try {
      const fd = new FormData();
      Object.entries(papier).forEach(([k, v]) => { if (v !== '') fd.append(k, v); });
      fd.append('scan', scan);
      const res = await api.post('/contrats-travail/rattacher-papier', fd,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      setShowRattacher(false);
      setScan(null);
      setPapier({ agent_id: '', type_contrat: 'CDI', date_signature: '', date_debut: '', date_fin: '',
        salaire_base: '', poste: '', lieu_travail: '', numero_cni: '' });
      if (res.data?.avertissement) window.alert(res.data.avertissement);
      loadAll();
    } catch (err) {
      const d = err.response?.data;
      setErreurPapier(
        d?.champs ? 'Champs manquants : ' + d.champs.join(' · ')
          : (d?.error || 'Le rattachement a échoué.')
      );
    } finally { setEnvoiPapier(false); }
  };
  const [form, setForm] = useState({ ...VIDE });
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  useEscapeClose(showForm, () => setShowForm(false));

  const loadAll = async () => {
    setLoading(true);
    try {
      const [c, f, a, al] = await Promise.all([
        api.get('/contrats-travail'),
        api.get('/postes'),
        api.get('/agents'),
        api.get('/contrats-travail/alertes'),
      ]);
      setContrats(c.data);
      // Le referentiel DU TENANT, meme source que « Gestion des postes ».
      setFiches(f.data?.postes || []);
      setAgents(a.data);
      setAlertes(al.data);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); }, []);

  const handleGenerer = async (e) => {
    e.preventDefault();
    setGenerating(true);
    setError('');
    try {
      const res = await api.post('/contrats-travail/generer', form);
      await apercuDocument(urlDocument('contrat-travail', res.data.id, 'pdf'));
      setShowForm(false);
      setForm({ ...VIDE });
      loadAll();
    } catch (err) {
      const data = err.response?.data;
      /* ⚠️ ON SE BRANCHE SUR LE `code`, PLUS SUR `error` — 18/10/2026. Le
       *    serveur rend desormais une PHRASE dans `error` (les 92 ecrans qui
       *    l affichent lisaient « champs_manquants » en clair). Le slug vit
       *    dans `code` : *un identifiant pour la machine, une phrase pour
       *    l humain, et jamais l un a la place de l autre.* */
      if (data?.code === 'CHAMPS_MANQUANTS' && data.champs) {
        setError(`Impossible de générer le contrat. Champs à renseigner : ${data.champs.join(', ')}`);
      } else {
        setError(data?.error || err.message || 'Erreur de génération');
      }
    } finally { setGenerating(false); }
  };

  const handleTraiterAlerte = async (id) => {
    try {
      await api.put(`/contrats-travail/alertes/${id}/traiter`);
      loadAll();
    } catch {}
  };

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  /* Le contrat REPREND le salaire de la fiche agent — une seule source.
   * Si la fiche n'en porte pas, le champ reste VIDE : on ne substitue rien.
   * `null` et `0` sont deux choses differentes ; seul `null` laisse vide. */
  const choisirAgent = (id) => {
    const a = agents.find(x => x.id === id);
    const sal = a && a.salaire_base !== null && a.salaire_base !== undefined ? a.salaire_base : '';
    setForm(f => ({ ...f, agent_id: id, salaire_base: sal }));
  };
  const agentChoisi = agents.find(a => a.id === form.agent_id) || null;
  const sansSalaireFiche = !!agentChoisi && (agentChoisi.salaire_base === null || agentChoisi.salaire_base === undefined);
  const isCDD = form.type_contrat === 'CDD';

  return (
    <div>
<div className="stats-grid">
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Contrats</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg></div></div>
          <div className="stat-card-value">{contrats.length}</div>
          <div className="stat-card-sub">generes</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Actifs</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg></div></div>
          <div className="stat-card-value" style={{ color: 'var(--gss-success)' }}>{contrats.filter(c => c.statut === 'actif').length}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Alertes</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg></div></div>
          <div className="stat-card-value" style={{ color: alertes.length > 0 ? 'var(--gss-danger)' : 'var(--gss-text-light)' }}>{alertes.length}</div>
          <div className="stat-card-sub">en cours</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header"><span className="stat-card-label">Postes</span><div className="stat-card-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/></svg></div></div>
          <div className="stat-card-value">{fiches.length}</div>
          <div className="stat-card-sub">au référentiel</div>
        </div>
      </div>

      {error && <div className="gss-alert gss-alert-error">{error}<button onClick={() => setError('')} style={{ float: 'right', background: 'none', border: 'none', cursor: 'pointer' }}>&times;</button></div>}

      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
        {[
          { id: 'contrats', label: 'Contrats' },
          { id: 'alertes', label: `Alertes (${alertes.length})` },
        ].map(t => (
          <button key={t.id} className={`gss-btn ${tab === t.id ? 'gss-btn-primary' : ''}`} onClick={() => setTab(t.id)}>{t.label}</button>
        ))}
        <div style={{ flex: 1 }} />
        {/* ⚠️ LE BOUTON N'EST PAS GRISE : IL N'EST PAS RENDU quand le droit du
            tenant n'a pas de modele. Le produit n'explique pas ses restrictions,
            mais ici l'absence serait incomprehensible : on dit donc la CAUSE
            (« aucun modele pour le Cameroun »), pas la restriction. */}
        {modele && modele.disponible && (
          <button className="gss-btn gss-btn-primary" onClick={() => setShowForm(true)}>+ Générer un contrat</button>
        )}
        {/* ⚠️ DEUX GESTES DIFFERENTS, DEUX BOUTONS. « Generer » PRODUIT une piece ;
            « Rattacher » CONSERVE une piece deja signee. Les confondre reviendrait
            a proposer de reecrire un contrat signe. */}
        <button className="gss-btn" style={{ marginLeft: modele && modele.disponible ? 8 : 0 }}
          onClick={() => setShowRattacher(true)}>
          Rattacher un contrat signé
        </button>
        {/* Le rattachement, lui, reste disponible partout : il ne depend
            d'aucun droit national — c'est le papier qui fait foi. */}
        {modele && !modele.disponible && (
          <span style={{
            marginLeft: 12, fontSize: 12, color: '#92400E', background: '#FFFBEB',
            border: '1px solid #FDE68A', borderRadius: 6, padding: '5px 10px',
          }}>
            {modele.message}
          </span>
        )}
      </div>

      {loading ? <p style={{ textAlign: 'center', padding: '2rem', color: 'var(--gss-text-light)' }}>Chargement...</p> : (
        <>
          {tab === 'contrats' && (
            <div className="content-card">
              <div className="content-card-title">Contrats de travail</div>
              {contrats.length === 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px' }}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#DDDDDD" strokeWidth="1.5" style={{ marginBottom: 16 }}>
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14,2 14,8 20,8"/>
                  </svg>
                  <div style={{ fontSize: 16, fontWeight: 600, color: '#666', marginBottom: 4 }}>Aucun contrat enregistré</div>
                  <div style={{ fontSize: 14, color: '#999' }}>Cliquez sur le bouton pour commencer</div>
                </div>
              ) : (
                <div className="gss-table-wrap">
                  <table className="gss-table">
                    <thead><tr><th>Numéro</th><th>Agent</th><th>Poste</th><th>Type</th><th>Debut</th><th>Fin essai</th><th>Salaire</th><th>Statut</th><th>Émis le</th><th>Documents</th></tr></thead>
                    <tbody>
                      {contrats.map(c => (
                        <tr key={c.id}>
                          <td><strong>{c.numero}</strong></td>
                          <td>{c.agent_prenom} {c.agent_nom}<div style={{ fontSize: '0.7rem', color: 'var(--gss-text-light)' }}>{c.matricule}</div></td>
                          <td style={{ textTransform: 'capitalize' }}>{c.poste?.replace(/_/g, ' ')}</td>
                          <td><span className="gss-badge" style={{ background: 'var(--gss-red-glow)', color: 'var(--gss-red)' }}>{c.type_contrat}</span></td>
                          <td style={{ fontSize: '0.78rem' }}>{c.date_debut?.split('T')[0]}</td>
                          <td style={{ fontSize: '0.78rem' }}>{c.date_fin_essai?.split('T')[0] || '-'} {expirationBadge(c.date_fin_essai || c.date_fin)}</td>
                          <td>{c.salaire_base ? Number(c.salaire_base).toLocaleString() + ' F' : '-'}</td>
                          <td><span className={`gss-badge ${c.statut === 'actif' ? 'gss-badge-green' : 'gss-badge-grey'}`}>{c.statut}</span></td>
                          {/* ⚠️ DATE D'EMISSION DE L'ARCHIVE — un ecran qui sert une archive dit
                              qu'il sert une archive, et donne sa date.
                              ⚠️ LA MENTION ENONCE UN FAIT VERIFIABLE, JAMAIS UN DIAGNOSTIC :
                              elle dit que le document est anterieur a la verification d'identite
                              du 31/08/2026, pas qu'il est fautif. La date exacte ou le generateur
                              est devenu tenant-aware reste une reserve ouverte depuis le 19/08 —
                              on ne peut donc pas accuser un document du 29/08. Seuil conservateur
                              assume : vrai pour tous, y compris ceux qui se reveleront corrects. */}
                          <td>
                            {c.created_at ? new Date(c.created_at).toLocaleDateString('fr-FR') : '-'}
                            {c.created_at && new Date(c.created_at) < SEUIL_VERIF_IDENTITE && (
                              <div style={{ fontSize: 10, color: '#8B1A1A', marginTop: 2, maxWidth: 200 }}>
                                ⚠ Emis avant la verification d'identite du 31/08/2026 — identite a controler avant transmission.
                              </div>
                            )}
                          </td>
                          <td style={{ display: 'flex', gap: 4 }}>
                            {/* ⚠️ CES BOUTONS SERVENT L'ARCHIVE, ILS NE REGENERENT RIEN.
                                L'archive d'un document emis ne se reecrit jamais : c'est une
                                piece. La ligne DIT donc qu'elle sert une archive, et donne sa
                                date d'emission (voir la colonne « Emis le »).
                                ⚠️ DEUX BOUTONS NE PORTENT JAMAIS LE MEME LIBELLE : le lot du
                                31/08 avait remplace « DOCX » par un « Telecharger » identique
                                a celui du PDF, effacant la distinction de format que l'ecran
                                portait depuis l'origine. Le format se lit sur le bouton.
                                ⚠️ Un format absent n'affiche PAS son bouton : pas de bouton
                                qui echoue. */}
                            {/* ⚠️ UN CONTRAT SIGNE SUR PAPIER N'A PAS DE BOUTON « REGENERER »,
                                ET IL N'EST PAS GRISE : IL N'EST PAS RENDU.
                                Le systeme CONSERVE un contrat signe, il ne le reecrit
                                jamais. Ce qui est propose ici, c'est le SCAN — la piece
                                elle-meme — et rien d'autre. */}
                            {c.origine === 'signe_papier' && c.document_id && (
                              <>
                                <button className="gss-btn-sm"
                                  onClick={() => apercuDocument(urlDocument('piece', c.document_id))}>
                                  Voir le scan
                                </button>
                                <span style={{
                                  fontSize: 11, color: '#1A8754', background: 'rgba(26,135,84,0.08)',
                                  border: '1px solid rgba(26,135,84,0.25)', borderRadius: 6,
                                  padding: '3px 10px', whiteSpace: 'nowrap', alignSelf: 'center',
                                }}>
                                  Signé le {c.date_signature ? new Date(c.date_signature).toLocaleDateString('fr-FR') : '—'}
                                </span>
                              </>
                            )}
                            {c.origine !== 'signe_papier' && c.document_url && (
                              <button className="gss-btn-sm" onClick={() => apercuDocument(urlDocument('contrat-travail', c.id, 'pdf'))}>Voir</button>
                            )}
                            {c.origine !== 'signe_papier' && c.document_url && (
                              <BoutonTelechargerDocument url={urlDocument('contrat-travail', c.id, 'pdf')} format="PDF" />
                            )}
                            {c.origine !== 'signe_papier' && c.docx_url && (
                              <BoutonTelechargerDocument url={urlDocument('contrat-travail', c.id, 'docx')} format="Word" />
                            )}
                            {/* ⚠️ UNE ABSENCE SE NOMME, ELLE NE SE SUBIT PAS.
                                Sans document, la cellule etait VIDE : l'utilisateur
                                constatait un trou sans comprendre pourquoi. La ligne
                                du contrat existe, le document reste a produire — et
                                l'ecran le DIT. (Retrait des documents anteriurs du
                                01/09 : les lignes restent, la reference est videe.) */}
                            {c.origine !== 'signe_papier' && !c.document_url && !c.docx_url && (
                              <span style={{
                                fontSize: 11.5, color: '#92400E', background: '#FFFBEB',
                                border: '1px solid #FDE68A', borderRadius: 6,
                                padding: '3px 10px', whiteSpace: 'nowrap', alignSelf: 'center',
                              }}>
                                Document à produire
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {tab === 'alertes' && (
            <div className="content-card">
              <div className="content-card-title">Alertes périodes d'essai et contrats</div>
              {alertes.length === 0 ? (
                <p style={{ color: 'var(--gss-text-light)', fontSize: '0.85rem' }}>Aucune alerte active</p>
              ) : (
                <div className="gss-table-wrap">
                  <table className="gss-table">
                    <thead><tr><th>Priorite</th><th>Titre</th><th>Agent</th><th>Échéance</th><th>Destinataires</th><th>Action</th></tr></thead>
                    <tbody>
                      {alertes.map(al => (
                        <tr key={al.id}>
                          <td>
                            <span className="gss-badge" style={{
                              background: al.priorite === 'critique' ? 'rgba(220,38,38,0.08)' : al.priorite === 'haute' ? 'rgba(217,119,6,0.08)' : 'rgba(0,0,0,0.04)',
                              color: al.priorite === 'critique' ? '#DC2626' : al.priorite === 'haute' ? '#D97706' : '#666'
                            }}>{al.priorite}</span>
                          </td>
                          <td><strong style={{ fontSize: '0.82rem' }}>{al.titre}</strong><div style={{ fontSize: '0.75rem', color: 'var(--gss-text-light)' }}>{al.description}</div></td>
                          <td>{al.agent_prenom ? `${al.agent_prenom} ${al.agent_nom}` : '-'}</td>
                          <td style={{ fontSize: '0.78rem', fontWeight: 600, color: new Date(al.date_echeance) < new Date() ? 'var(--gss-danger)' : 'var(--gss-warning)' }}>{al.date_echeance?.split('T')[0]}</td>
                          <td style={{ fontSize: '0.72rem', textTransform: 'uppercase', color: 'var(--gss-text-light)' }}>{al.destinataires_roles?.join(', ')}</td>
                          <td><button className="gss-btn-sm" onClick={() => handleTraiterAlerte(al.id)}>Traiter</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}


        </>
      )}

      {showForm && (
        <div className="gss-modal-overlay" onClick={() => setShowForm(false)}>
          <div className="gss-modal gss-modal-lg" onClick={e => e.stopPropagation()}>
            <div className="gss-modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                <h2>Générer un contrat de travail</h2>
              </div>
              <div className="gss-modal-subtitle">Configuration du contrat</div>
              <button className="gss-modal-close" onClick={() => setShowForm(false)}>&times;</button>
            </div>
            <form onSubmit={handleGenerer}>
              <div style={{ padding: '1.5rem' }}>
                {/* Section: Agent & Poste */}
                <div className="gss-modal-section">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/>
                  </svg>
                  <span>AGENT & POSTE</span>
                </div>
                <div className="gss-form-grid">
                  <div className="gss-field gss-field-full">
                    <label>Agent *</label>
                    <select className="gss-input" required value={form.agent_id} onChange={e => choisirAgent(e.target.value)}>
                      <option value="">Sélectionner un agent</option>
                      {agents.filter(a => a.statut === 'actif').map(a => (
                        <option key={a.id} value={a.id}>{a.matricule} — {a.prenom} {a.nom}</option>
                      ))}
                    </select>
                  </div>
                  <div className="gss-field">
                    <label>Poste *</label>
                    <select className="gss-input" required value={form.poste} onChange={e => setField('poste', e.target.value)}>
                      <option value="">— choisir un poste —</option>
                      {fiches.map(f => (
                        <option key={f.code} value={f.code}>{f.titre}</option>
                      ))}
                    </select>
                    {/* ⚠️ La duree d'essai n'est plus affichee ici. Elle etait
                        lue dans une table en dur, avec un repli qui inventait
                        « 1 + 1 mois » pour tout poste inconnu. Elle se regle et
                        se controle sur le poste, dans « Gestion des postes ». */}
                  </div>
                  <div className="gss-field">
                    <label>Numéro CNI</label>
                    <input className="gss-input" value={form.numero_cni} onChange={e => setField('numero_cni', e.target.value)} />
                  </div>
                </div>

                {/* Section: Contrat */}
                <div className="gss-modal-section">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8B1A1A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/>
                  </svg>
                  <span>CONDITIONS DU CONTRAT</span>
                </div>
                <div className="gss-form-grid">
                  <div className="gss-field">
                    <label>Type de contrat *</label>
                    <select className="gss-input" required value={form.type_contrat} onChange={e => setField('type_contrat', e.target.value)}>
                      <option value="CDI">CDI</option>
                      <option value="CDD">CDD</option>
                      <option value="Periode_essai">Période d'essai</option>
                      <option value="Interim">Interim</option>
                      <option value="Stage">Stage</option>
                    </select>
                  </div>
                  <div className="gss-field">
                    <label>Date de debut *</label>
                    <input className="gss-input" type="date" required value={form.date_debut} onChange={e => setField('date_debut', e.target.value)} />
                  </div>
                  {isCDD && (
                    <div className="gss-field">
                      <label>Date de fin *</label>
                      <input className="gss-input" type="date" required value={form.date_fin} onChange={e => setField('date_fin', e.target.value)} />
                    </div>
                  )}
                  <div className="gss-field">
                    {/* ⚠️ Le libellé de devise vient du tenant (`branding.devise`),
                        il n'est plus écrit « FCFA » en dur : VIGIE est en XAF. */}
                    <label>Salaire mensuel{devise ? ` (${devise})` : ''} *</label>
                    <input className="gss-input" type="number" required
                      value={form.salaire_base}
                      placeholder={agentChoisi ? '' : "Choisir l'agent d'abord"}
                      onChange={e => setField('salaire_base', e.target.value)} />
                    {/* ⚠️ ON LE DIT. Avant, le champ arrivait pré-rempli à 52 500 et
                        personne ne savait si la valeur avait été décidée ou héritée. */}
                    {sansSalaireFiche && (
                      <div style={{ fontSize: 11, color: 'var(--gss-danger)', marginTop: 4 }}>
                        La fiche de cet agent ne porte aucun salaire. Saisissez celui du contrat —
                        aucune valeur n'est proposée par défaut.
                      </div>
                    )}
                  </div>
                  <div className="gss-field">
                    <label>Lieu de travail</label>
                    <input className="gss-input" value={form.lieu_travail}
                      placeholder={ville || ''}
                      onChange={e => setField('lieu_travail', e.target.value)} />
                  </div>
                  <div className="gss-field gss-field-full">
                    <label>Clauses particulieres</label>
                    <textarea className="gss-input" rows="3" value={form.clauses_particulieres} onChange={e => setField('clauses_particulieres', e.target.value)} style={{ resize: 'vertical' }} />
                  </div>
                </div>
              </div>
              <div className="gss-modal-actions">
                <button type="button" className="gss-btn-cancel" onClick={() => setShowForm(false)}>Annuler</button>
                <button type="submit" className="gss-btn-deploy" disabled={generating}>
                  {generating ? 'Generation PDF...' : <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 2L11 13"/><path d="M22 2L15 22l-4-9-9-4z"/></svg> Générer le contrat PDF</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════
          RATTACHER UN CONTRAT SIGNÉ SUR PAPIER
          ═══════════════════════════════════════════════════════════════════
          ⚠️ CET ÉCRAN NE PRODUIT RIEN. Il CONSERVE une pièce déjà signée.
             Aucun PDF n'est généré, aucun modèle n'est appliqué : le document
             qui fait foi est le scan déposé. */}
      {showRattacher && (
        <div className="gss-modal-overlay" onClick={() => setShowRattacher(false)}>
          <div className="gss-modal gss-modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="gss-modal-header">
              <h2>Rattacher un contrat signé</h2>
              <div className="gss-modal-subtitle">
                Le système conserve le contrat signé — il ne le réécrit jamais.
              </div>
              <button className="gss-modal-close" onClick={() => setShowRattacher(false)}>&times;</button>
            </div>
            {erreurPapier && (
              <div style={{ padding: '0.6rem 1.5rem', color: 'var(--gss-danger)', fontSize: '0.9rem' }}>
                {erreurPapier}
              </div>
            )}
            <form onSubmit={soumettrePapier}>
              <div className="gss-form-grid" style={{ padding: '1.5rem' }}>
                <div className="gss-field gss-field-full">
                  <label>Agent *</label>
                  <select className="gss-input" required value={papier.agent_id}
                    onChange={(e) => setPapier({ ...papier, agent_id: e.target.value })}>
                    <option value="">Sélectionner un agent</option>
                    {agents.filter((a) => a.statut === 'actif').map((a) => (
                      <option key={a.id} value={a.id}>{a.matricule} — {a.prenom} {a.nom}</option>
                    ))}
                  </select>
                </div>
                <div className="gss-field">
                  <label>Type de contrat *</label>
                  <select className="gss-input" required value={papier.type_contrat}
                    onChange={(e) => setPapier({ ...papier, type_contrat: e.target.value })}>
                    <option value="CDI">CDI</option>
                    <option value="CDD">CDD</option>
                  </select>
                </div>
                {/* ══════════════════════════════════════════════════════════
                    🔴 CE CHAMP N'EXISTAIT PAS — ET LE POSTE PARTAIT QUAND MÊME
                    ══════════════════════════════════════════════════════════
                    ⚠️ CE FORMULAIRE N'A JAMAIS DEMANDÉ LE POSTE : il envoyait
                       `agent_securite`, écrit en dur dans son état initial, sans
                       le montrer à personne. Le contrat enregistré portait donc
                       un emploi que la RH n'avait pas choisi — et chez un client
                       qui n'a pas ce code, la clé étrangère l'aurait refusé.
                    ➡️ DÉCISION DE DOM, 13/10/2026 : « Poste exigé au contrat
                       papier : oui — un contrat porte l'emploi. » Le champ est
                       donc visible, obligatoire, et sa liste vient de `/postes`,
                       le référentiel DU TENANT. **C'est le seul champ ajouté à un
                       écran par ce lot hors des maquettes ① et ②**, et il est la
                       conséquence directe de cette décision : sans lui, l'écran
                       refuserait chaque envoi.
                    ⓘ Même rédaction que le formulaire de GÉNÉRATION juste
                      au-dessus — un seul énoncé pour une seule notion. */}
                <div className="gss-field">
                  <label>Poste porté au contrat *</label>
                  <select className="gss-input" required value={papier.poste}
                    onChange={(e) => setPapier({ ...papier, poste: e.target.value })}>
                    <option value="">— choisir un poste —</option>
                    {fiches.map((f) => (
                      <option key={f.code} value={f.code}>{f.titre}</option>
                    ))}
                  </select>
                  {/* ⛔ UNE LISTE VIDE SE DIT. Sans cette phrase, la RH verrait un
                      menu sans option et croirait à une panne, alors qu'il manque
                      un préalable qu'elle peut faire elle-même. */}
                  {fiches.length === 0 && (
                    <div style={{ fontSize: 11, color: '#B45309', marginTop: 4 }}>
                      Aucun poste n’est déclaré dans cette société. Les postes se
                      déclarent dans « Gestion des postes », et le contrat ne peut pas
                      porter un emploi que la société n’a pas défini.
                    </div>
                  )}
                  <div style={{ fontSize: 11, color: 'var(--gss-text-light)', marginTop: 4 }}>
                    Celui qui figure sur le papier signé. Il ouvre la situation du
                    salarié à la <strong>date d’effet</strong> ci-dessous, pas à la date
                    de signature.
                  </div>
                </div>
                <div className="gss-field">
                  <label>Date de signature *</label>
                  <input className="gss-input" type="date" required value={papier.date_signature}
                    onChange={(e) => setPapier({ ...papier, date_signature: e.target.value })} />
                </div>
                <div className="gss-field">
                  <label>Date d'effet *</label>
                  <input className="gss-input" type="date" required value={papier.date_debut}
                    onChange={(e) => setPapier({ ...papier, date_debut: e.target.value })} />
                </div>
                {papier.type_contrat === 'CDD' && (
                  <div className="gss-field">
                    <label>Date de fin *</label>
                    <input className="gss-input" type="date" required value={papier.date_fin}
                      onChange={(e) => setPapier({ ...papier, date_fin: e.target.value })} />
                  </div>
                )}
                <div className="gss-field">
                  <label>Salaire porté au contrat{devise ? ` (${devise})` : ''} *</label>
                  <input className="gss-input" type="number" required value={papier.salaire_base}
                    onChange={(e) => setPapier({ ...papier, salaire_base: e.target.value })} />
                  {/* ⚠️ ON LE DIT À L'ÉCRAN. C'est le salaire DU PAPIER : il peut
                      différer de celui de la fiche, et ce n'est pas une erreur. */}
                  <div style={{ fontSize: 11, color: 'var(--gss-text-light)', marginTop: 4 }}>
                    Celui qui figure sur le papier signé. Il peut différer du salaire actuel
                    de la fiche — les deux ne se corrigent pas l'un par l'autre.
                  </div>
                </div>
                <div className="gss-field">
                  <label>Lieu de travail</label>
                  <input className="gss-input" value={papier.lieu_travail}
                    placeholder={ville || ''}
                    onChange={(e) => setPapier({ ...papier, lieu_travail: e.target.value })} />
                </div>
                <div className="gss-field gss-field-full">
                  <label>
                    Scan du contrat signé *
                    {regleScan && ` (${regleScan.libelle_formats}, maximum ${formaterPoids(regleScan.octets)})`}
                  </label>
                  <input type="file" className="gss-input" style={{ paddingTop: 10 }} required
                    accept={regleScan ? regleScan.extensions.join(',') : '.pdf,.jpg,.jpeg,.png'}
                    onChange={choisirScan} />
                  <div style={{ fontSize: 11, color: 'var(--gss-text-light)', marginTop: 4 }}>
                    Obligatoire : un contrat papier sans scan n'est rattaché à rien.
                    Le scan n'est jamais remplacé — une correction se dépose comme une
                    nouvelle version, l'ancienne reste.
                  </div>
                </div>
              </div>
              <div className="gss-modal-actions">
                <button type="button" className="gss-btn-cancel" onClick={() => setShowRattacher(false)}>Annuler</button>
                <button type="submit" className="gss-btn-deploy" disabled={envoiPapier}>
                  {envoiPapier ? 'Rattachement…' : 'Rattacher le contrat'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
